import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ChatPanel } from './ChatPanel';
import { SearchPalette } from './SearchPalette';
import { WeldSessionChip } from './WeldSessionChip';
import { leadListApi } from '../services/leadListApi';

// Shell chrome for every /mi/* route. Sidebar nav + topbar.
//
// The sidebar lists only routes that ship. Build call list and watch
// list functionality lives on AOI detail (per-party push) and pool-
// order detail (unmatched names) — no standalone routes exist yet
// and we keep the nav honest by not advertising them.

interface ShellProps {
  children: ReactNode;
}

// Sectioned nav: top-level item (Brief / Inbox) plus two jurisdiction
// sections (Weld / Wyoming). The badge counts on each section's Lead
// list query separately (jurisdiction=CO vs WY) — see leadListBadge
// effect below.
interface NavLeaf {
  to: string;
  label: string;
  badge?: 'lead_list_co' | 'lead_list_wy';
}
interface NavSection {
  heading?: string;     // omit for the top-level group
  items: NavLeaf[];
}
const NAV: NavSection[] = [
  { items: [
    { to: '/mi/brief', label: 'Brief' },
    { to: '/mi/inbox', label: 'Inbox' },
  ] },
  { heading: 'Weld', items: [
    { to: '/mi/aois',        label: 'AOIs' },
    { to: '/mi/pool-orders', label: 'Pool orders' },
    { to: '/mi/leads',       label: 'Lead list', badge: 'lead_list_co' },
  ] },
  { heading: 'Wyoming', items: [
    { to: '/mi/wy/aois',     label: 'AOIs' },
    { to: '/mi/wy/orders',   label: 'Orders' },
    { to: '/mi/wy/leads',    label: 'Lead list', badge: 'lead_list_wy' },
  ] },
];

export function Shell({ children }: ShellProps) {
  const loc = useLocation();
  const nav = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  // Lead-list badge counts: one per jurisdiction. Both refresh on
  // route change. Cheap enough — the LeadList page itself drives most
  // navigation that affects these numbers, and the GET is small.
  const [leadListCo, setLeadListCo] = useState<number | null>(null);
  const [leadListWy, setLeadListWy] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      leadListApi.get(['pending', 'researched'], { jurisdiction: 'CO' }),
      leadListApi.get(['pending', 'researched'], { jurisdiction: 'WY' }),
    ]).then(([co, wy]) => {
      if (cancelled) return;
      setLeadListCo((co.counts.pending ?? 0) + (co.counts.researched ?? 0));
      setLeadListWy((wy.counts.pending ?? 0) + (wy.counts.researched ?? 0));
    }).catch(() => { /* badges optional; silent failure */ });
    return () => { cancelled = true; };
  }, [loc.pathname]);
  const badgeNumber = (b: NavLeaf['badge']) =>
    b === 'lead_list_co' ? leadListCo
    : b === 'lead_list_wy' ? leadListWy
    : null;

  return (
    <div className="flex h-screen w-screen bg-bg text-text font-body">
      <aside className="w-[220px] shrink-0 border-r border-border bg-bg-elev/60 flex flex-col">
        <div className="px-5 py-5 border-b border-border">
          <div className="font-display text-[20px] leading-none text-accent-bright tracking-tight">Timberline</div>
          <div className="font-mono text-[10px] text-text-faint mt-1.5 uppercase tracking-wider">Mineral Intelligence</div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map((section, sIdx) => (
            <div key={sIdx} className={sIdx > 0 ? 'mt-4' : ''}>
              {section.heading && (
                <div className="px-5 py-1 text-[10px] font-mono uppercase tracking-wider text-text-faint">
                  {section.heading}
                </div>
              )}
              {section.items.map(item => {
                const badge = badgeNumber(item.badge);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-5 py-2.5 text-[13px] border-l-2 ${
                        isActive
                          ? 'text-accent-bright border-accent bg-accent-soft'
                          : 'text-text-dim border-transparent hover:text-text hover:bg-bg-hover/40'
                      }`
                    }
                  >
                    <span>{item.label}</span>
                    {badge !== null && badge > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-mono rounded bg-accent/20 text-accent-bright">{badge}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-border text-[11px] text-text-faint font-mono">
          weld county · v0.1
        </div>
      </aside>
      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="h-12 shrink-0 border-b border-border flex items-center px-6 bg-bg-elev/40">
          <div className="font-mono text-[11px] text-text-faint">
            <span className="text-text-dim">workspace</span>
            <span className="mx-2 text-text-ghost">/</span>
            <span className="text-text">Mineral Intelligence</span>
            <span className="mx-2 text-text-ghost">/</span>
            <span className="text-accent-bright">{breadcrumb(loc.pathname)}</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {/* Search trigger — looks like an input, opens the palette
                on click. Hotkey label hints at Cmd-K / Ctrl-K (the
                palette listener is global so the hotkey works
                anywhere). For the dedicated full-results page,
                navigate to /mi/search. */}
            <button
              onClick={() => nav('/mi/search')}
              title="Open search results page (Cmd-K / Ctrl-K opens the quick palette)"
              className="flex items-center gap-2 text-[11px] font-mono text-text-faint hover:text-text border border-border hover:border-border-bright rounded px-2.5 py-1"
            >
              <span className="text-text-faint">⌕</span>
              <span>Search parties</span>
              <span className="text-text-ghost ml-2">⌘K</span>
            </button>
            <button
              onClick={() => setChatOpen(o => !o)}
              className={
                'text-[11px] font-mono px-2.5 py-1 rounded border ' +
                (chatOpen
                  ? 'border-accent text-accent bg-accent-soft'
                  : 'border-border text-text-faint hover:text-text hover:border-border-bright')
              }>
              {chatOpen ? '✕ Close chat' : '✦ Ask Claude'}
            </button>
            <WeldSessionChip />
            <NavLink to="/select" className="text-[11px] text-text-faint hover:text-text-dim font-mono">↔ Switch workspace</NavLink>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto relative z-10">{children}</div>
      </main>
      <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />
      <SearchPalette />
    </div>
  );
}

function breadcrumb(pathname: string): string {
  // Wyoming patterns come first so /mi/wy/aoi/:id doesn't fall
  // through to the generic /mi/aoi/ Weld matcher below.
  if (pathname.startsWith('/mi/wy/aoi/'))        return 'Wyoming · AOI detail';
  if (pathname.startsWith('/mi/wy/aois'))        return 'Wyoming · AOIs';
  if (pathname.startsWith('/mi/wy/orders/'))     return 'Wyoming · Order detail';
  if (pathname.startsWith('/mi/wy/orders'))      return 'Wyoming · Orders';
  if (pathname.startsWith('/mi/wy/leads'))       return 'Wyoming · Lead list';
  if (pathname.startsWith('/mi/search'))         return 'Search';
  if (pathname.startsWith('/mi/aoi/'))           return 'AOI detail';
  if (pathname.startsWith('/mi/aois'))           return 'AOIs';
  if (pathname.startsWith('/mi/brief'))          return 'Brief';
  if (pathname.startsWith('/mi/pool-orders/'))   return 'Pool order detail';
  if (pathname.startsWith('/mi/pool-orders'))    return 'Pool orders';
  if (pathname.startsWith('/mi/leads'))          return 'Lead list';
  if (pathname.startsWith('/mi/inbox'))          return 'Inbox';
  return '';
}
